import { lazy, Suspense, useEffect, useState, type ComponentType } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { engancharLinksDeLaApp } from "./lib/linksDeLaApp";
import { PwaUpdateNotice } from "./components/PwaUpdateNotice";
import { BiometricLockGate } from "./components/BiometricLockGate";
// Directo y no diferido: el banner corre en TODAS las pantallas y la entrada
// es una redirección de dos líneas — un chunk aparte sería más caro que el código.
import { VerComoBanner, VerComoEntrada } from "./pages/VerComoPage";
import { EntrarAppEntrada } from "./pages/EntrarAppPage";
import { Spinner } from "./components/ui/Spinner";
import { isStandalone } from "./lib/install";
import { useAuth } from "./auth/AuthContext";
import { getAccessToken } from "./api/client";
import { tieneRespuestaPendiente } from "./auth/replyGate";

// El Layout del organizador y los guardianes de ruta también son diferidos:
// lo que App.tsx importe estático termina en la entrada, y la entrada cambia
// en cada deploy.
const Layout = diferida(() => import("./components/Layout"), "Layout");
const InicioRedirect = diferida(() => import("./components/InicioRedirect"), "InicioRedirect");
const ProtectedRoute = diferida(() => import("./components/ProtectedRoute"), "ProtectedRoute");

/**
 * Vistas de desarrollo (y el modal que una de ellas muestra). En producción
 * `import.meta.env.DEV` es false, la rama muerta se elimina al compilar y con
 * ella el import(): no queda ningún chunk suyo.
 *
 * Antes eran imports estáticos. Como cada vista importa la pantalla REAL que
 * muestra, arrastraban medio código de la app al archivo de entrada —que es el
 * que tiene la lista de pantallas y cambia con cualquier cambio—, y tocar un
 * texto en UNA pantalla obligaba a rebajar 50 archivos (452 KB, medido).
 */
/** En producción ocupa el lugar de la vista. `never` para que el ternario tome el tipo del componente real. */
const Nada = (() => null) as unknown as never;

const EditTournamentModal = import.meta.env.DEV ? diferida(() => import("./components/EditTournamentModal"), "EditTournamentModal") : Nada;
const VistaVozPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaVozPreview"), "VistaVozPreview") : Nada;
const VistaBienvenidaPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaBienvenidaPreview"), "VistaBienvenidaPreview") : Nada;
const VistaFechaOcultaPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaFechaOcultaPreview"), "VistaFechaOcultaPreview") : Nada;
const VistaFiguraPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaFiguraPreview"), "VistaFiguraPreview") : Nada;
const VistaAsistenciaPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaAsistenciaPreview"), "VistaAsistenciaPreview") : Nada;
const VistaEquipoIdealPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaEquipoIdealPreview"), "VistaEquipoIdealPreview") : Nada;
const VistaCardPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaCardPreview"), "VistaCardPreview") : Nada;
const VistaCardJugadorPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaCardJugadorPreview"), "VistaCardJugadorPreview") : Nada;
const VistaOrganizadorPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaOrganizadorPreview"), "VistaOrganizadorPreview") : Nada;
const VistaCotizadorPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaCotizadorPreview"), "VistaCotizadorPreview") : Nada;
const VistaCobrosPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaCobrosPreview"), "VistaCobrosPreview") : Nada;
const VistaPagarPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaPagarPreview"), "VistaPagarPreview") : Nada;
const VistaIncidenciasPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaIncidenciasPreview"), "VistaIncidenciasPreview") : Nada;
const VistaComunidadPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaComunidadPreview"), "VistaComunidadPreview") : Nada;
const VistaPerfilComunidadPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaPerfilComunidadPreview"), "VistaPerfilComunidadPreview") : Nada;
const VistaFotoPerfilPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaFotoPerfilPreview"), "VistaFotoPerfilPreview") : Nada;
const VistaCargaPartidoPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaCargaPartidoPreview"), "VistaCargaPartidoPreview") : Nada;
const VistaResultadoWebPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaResultadoWebPreview"), "VistaResultadoWebPreview") : Nada;
const VistaUnirsePreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaUnirsePreview"), "VistaUnirsePreview") : Nada;
const VistaDisciplinaPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaDisciplinaPreview"), "VistaDisciplinaPreview") : Nada;
const VistaCuerpoTecnicoPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaCuerpoTecnicoPreview"), "VistaCuerpoTecnicoPreview") : Nada;
const VistaPlantelPanelPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaPlantelPanelPreview"), "VistaPlantelPanelPreview") : Nada;
const VistaArbitrosPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaArbitrosPreview"), "VistaArbitrosPreview") : Nada;
const VistaAgendaArbitroPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaAgendaArbitroPreview"), "VistaAgendaArbitroPreview") : Nada;
const VistaFotosArchivoPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaFotosArchivoPreview"), "VistaFotosArchivoPreview") : Nada;
const VistaTorneosPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaTorneosPreview"), "VistaTorneosPreview") : Nada;
const VistaTorneoNuevoPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaTorneoNuevoPreview"), "VistaTorneoNuevoPreview") : Nada;
const VistaEscudoEquipoPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaEscudoEquipoPreview"), "VistaEscudoEquipoPreview") : Nada;
const VistaEditarTorneoPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaEditarTorneoPreview"), "VistaEditarTorneoPreview") : Nada;
const VistaFaseFinalPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaFaseFinalPreview"), "VistaFaseFinalPreview") : Nada;
const VistaLoQueSiguePreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaLoQueSiguePreview"), "VistaLoQueSiguePreview") : Nada;
const VistaPanelTorneoPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaPanelTorneoPreview"), "VistaPanelTorneoPreview") : Nada;
const VistaSiguienteTorneoPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaSiguienteTorneoPreview"), "VistaSiguienteTorneoPreview") : Nada;
const VistaEditarCrucePreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaEditarCrucePreview"), "VistaEditarCrucePreview") : Nada;
const VistaPlanillaPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaPlanillaPreview"), "VistaPlanillaPreview") : Nada;
const VistaPlanillasExportPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaPlanillasExportPreview"), "VistaPlanillasExportPreview") : Nada;
const VistaImportarFechaPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaImportarFechaPreview"), "VistaImportarFechaPreview") : Nada;
const VistaDesprogramarPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaDesprogramarPreview"), "VistaDesprogramarPreview") : Nada;
const VistaProgramarFechaPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaProgramarFechaPreview"), "VistaProgramarFechaPreview") : Nada;
const VistaJornadaPanelPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaJornadaPanelPreview"), "VistaJornadaPanelPreview") : Nada;
const VistaPwaProgramacionPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaPwaProgramacionPreview"), "VistaPwaProgramacionPreview") : Nada;
const VistaAutoProgramarPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaAutoProgramarPreview"), "VistaAutoProgramarPreview") : Nada;
const VistaGruposFechaPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaGruposFechaPreview"), "VistaGruposFechaPreview") : Nada;
const VistaFixtureVacioPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaFixtureVacioPreview"), "VistaFixtureVacioPreview") : Nada;
const VistaRetiroPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaRetiroPreview"), "VistaRetiroPreview") : Nada;
const VistaHomeJugadorPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaHomeJugadorPreview"), "VistaHomeJugadorPreview") : Nada;
const VistaLigaJugadorPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaLigaJugadorPreview"), "VistaLigaJugadorPreview") : Nada;
const VistaPartidoJugadorPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaPartidoJugadorPreview"), "VistaPartidoJugadorPreview") : Nada;
const VistaTarjetaJugadorPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaTarjetaJugadorPreview"), "VistaTarjetaJugadorPreview") : Nada;
const VistaTarjetasLigaPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaTarjetasLigaPreview"), "VistaTarjetasLigaPreview") : Nada;
const VistaEncuadreTopePreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaEncuadreTopePreview"), "VistaEncuadreTopePreview") : Nada;
const VistaReversoPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaReversoPreview"), "VistaReversoPreview") : Nada;
const VistaEquipoJugadorPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaEquipoJugadorPreview"), "VistaEquipoJugadorPreview") : Nada;
const VistaFotoEquipoPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaFotoEquipoPreview"), "VistaFotoEquipoPreview") : Nada;
const VistaAvisosPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaAvisosPreview"), "VistaAvisosPreview") : Nada;
const VistaSelectorTorneoPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaSelectorTorneoPreview"), "VistaSelectorTorneoPreview") : Nada;
const VistaBancoFotosPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaBancoFotosPreview"), "VistaBancoFotosPreview") : Nada;
const VistaArbitroPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaArbitroPreview"), "VistaArbitroPreview") : Nada;
const VistaAdminLigasPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaAdminLigasPreview"), "VistaAdminLigasPreview") : Nada;
const VistaBorrarLigaPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaBorrarLigaPreview"), "VistaBorrarLigaPreview") : Nada;
const VistaMensajeObligatorioPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaMensajeObligatorioPreview"), "VistaMensajeObligatorioPreview") : Nada;
const VistaGateObligatorioPreview = import.meta.env.DEV ? diferida(() => import("./pages/VistaMensajeObligatorioPreview"), "VistaGateObligatorioPreview") : Nada;


/**
 * Cada pantalla es un chunk aparte que se baja al abrirla. Antes TODO el
 * código (organizador, jugador, árbitro, admin) iba en un único index-*.js de
 * 1,5 MB: cualquier push a web/ cambiaba su hash y cada usuario lo rebajaba
 * entero. Ahora un cambio en una pantalla sólo invalida su chunk (sept 2026,
 * ancho de banda de Render agotado).
 *
 * Las vistas de desarrollo (Vista*Preview) van con import() DENTRO de la rama
 * de import.meta.env.DEV — ver el bloque de abajo. Así el build de producción
 * borra la rama y con ella el import(), y no queda ningún chunk suyo. (Este
 * comentario decía lo contrario: que iban con import estático. Eso era lo que
 * arrastraba medio código a la entrada; corregido al auditar.)
 */
function diferida<M extends Record<string, unknown>, K extends keyof M & string>(importar: () => Promise<M>, nombre: K): M[K] {
  return lazy(async () => {
    const modulo = await importar();
    // Sin módulo = el chunk de una versión vieja ya no existe y main.tsx está
    // recargando la página (vite:preloadError): se queda en la espera en vez
    // de mostrar un error que dura medio segundo.
    if (!modulo) return new Promise<never>(() => {});
    return { default: modulo[nombre] as ComponentType<Record<string, unknown>> };
    // El cast devuelve el tipo del componente original: las pantallas con
    // props (el modal de las vistas) siguen chequeando sus props.
  }) as unknown as M[K];
}

/** Espera mientras baja el chunk: nada los primeros 300 ms (con el precache es instantáneo). */
function EsperaDePantalla() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setVisible(true), 300);
    return () => window.clearTimeout(t);
  }, []);
  return (
    <div className="min-h-full flex items-center justify-center" aria-busy="true">
      {visible && <Spinner size="lg" />}
    </div>
  );
}

const HomePage = diferida(() => import("./pages/HomePage"), "HomePage");
const LoginPage = diferida(() => import("./pages/LoginPage"), "LoginPage");
const OnboardingPage = diferida(() => import("./pages/OnboardingPage"), "OnboardingPage");
const AccountDeletionPage = diferida(() => import("./pages/LegalPage"), "AccountDeletionPage");
const PrivacyPage = diferida(() => import("./pages/LegalPage"), "PrivacyPage");
const TermsPage = diferida(() => import("./pages/LegalPage"), "TermsPage");
const PublicTournamentPage = diferida(() => import("./pages/PublicTournamentPage"), "PublicTournamentPage");
const PagarPage = diferida(() => import("./pages/PagarPage"), "PagarPage");
const ProfilePage = diferida(() => import("./pages/ProfilePage"), "ProfilePage");
const TournamentsPage = diferida(() => import("./pages/TournamentsPage"), "TournamentsPage");
const NewTournamentPage = diferida(() => import("./pages/NewTournamentPage"), "NewTournamentPage");
const TournamentWorkspacePage = diferida(() => import("./pages/TournamentWorkspacePage"), "TournamentWorkspacePage");
const JornadaPanelPage = diferida(() => import("./pages/JornadaPanelPage"), "JornadaPanelPage");
// Consola de fotos y tarjetas: todas las ligas del organizador (docs/PLAN_CONSOLA_FOTOS.md).
const FotosPanelPage = diferida(() => import("./pages/FotosPanelPage"), "FotosPanelPage");
const PlayerShell = diferida(() => import("./pages/player/PlayerShell"), "PlayerShell");
const PlayerHomePage = diferida(() => import("./pages/player/PlayerHomePage"), "PlayerHomePage");
const PlayerLeaguePage = diferida(() => import("./pages/player/PlayerLeaguePage"), "PlayerLeaguePage");
const PlayerCardPage = diferida(() => import("./pages/player/PlayerCardPage"), "PlayerCardPage");
const PlayerDiagnosticoPage = diferida(() => import("./pages/player/PlayerDiagnosticoPage"), "PlayerDiagnosticoPage");
const PlayerInstallPage = diferida(() => import("./pages/player/PlayerInstallPage"), "PlayerInstallPage");
const AdminPage = diferida(() => import("./pages/AdminPage"), "AdminPage");
const InstallPage = diferida(() => import("./pages/InstallPage"), "InstallPage");
const PlayerRegisterPage = diferida(() => import("./pages/player/PlayerRegisterPage"), "PlayerRegisterPage");
const FanJoinPage = diferida(() => import("./pages/player/FanJoinPage"), "FanJoinPage");
const PublicTeamPage = diferida(() => import("./pages/player/PublicTeamPage"), "PublicTeamPage");
const PlayerJoinPage = diferida(() => import("./pages/player/PlayerJoinPage"), "PlayerJoinPage");
const PlayerPhotoPickPage = diferida(() => import("./pages/player/PlayerPhotoPickPage"), "PlayerPhotoPickPage");
const DownloadAppPage = diferida(() => import("./pages/player/DownloadAppPage"), "DownloadAppPage");
const PlayerMatchPage = diferida(() => import("./pages/player/PlayerMatchPage"), "PlayerMatchPage");
const PlayerTeamPage = diferida(() => import("./pages/player/PlayerTeamPage"), "PlayerTeamPage");
const PublicPlayerProfilePage = diferida(() => import("./pages/player/PublicPlayerProfilePage"), "PublicPlayerProfilePage");
const CommunityPage = diferida(() => import("./pages/player/CommunityPage"), "CommunityPage");
const CommunityMessagesPage = diferida(() => import("./pages/player/CommunityMessagesPage"), "CommunityMessagesPage");
const CommunityChatPage = diferida(() => import("./pages/player/CommunityChatPage"), "CommunityChatPage");
const CommunityProfilePage = diferida(() => import("./pages/player/CommunityProfilePage"), "CommunityProfilePage");
const PlayerProfilePage = diferida(() => import("./pages/player/PlayerProfilePage"), "PlayerProfilePage");
const RefereeMatchLinkPage = diferida(() => import("./pages/RefereeMatchLinkPage"), "RefereeMatchLinkPage");
const RefereeAgendaPage = diferida(() => import("./pages/referee/RefereeAgendaPage"), "RefereeAgendaPage");
const RefereeMatchPage = diferida(() => import("./pages/referee/RefereeMatchPage"), "RefereeMatchPage");
const RefereePerfilPage = diferida(() => import("./pages/referee/RefereePerfilPage"), "RefereePerfilPage");
const InternalMessagesPage = diferida(() => import("./pages/InternalMessagesPage"), "InternalMessagesPage");
const OrganizerAppShell = diferida(() => import("./pages/organizer/OrganizerAppShell"), "OrganizerAppShell");
const OrganizerEquipoIdealStandalone = diferida(() => import("./pages/organizer/OrganizerEquipoIdealStandalone"), "OrganizerEquipoIdealStandalone");
const OrganizerEscudoStandalone = diferida(() => import("./pages/organizer/OrganizerEscudoStandalone"), "OrganizerEscudoStandalone");
const OrganizerInicioPage = diferida(() => import("./pages/organizer/OrganizerInicioPage"), "OrganizerInicioPage");
const OrganizerFechaPage = diferida(() => import("./pages/organizer/OrganizerFechaPage"), "OrganizerFechaPage");
const OrganizerLigaPage = diferida(() => import("./pages/organizer/OrganizerLigaPage"), "OrganizerLigaPage");
const OrganizerActividadPage = diferida(() => import("./pages/organizer/OrganizerActividadPage"), "OrganizerActividadPage");
const OrganizerInviteJoinPage = diferida(() => import("./pages/organizer/OrganizerInviteJoinPage"), "OrganizerInviteJoinPage");

/** La cuenta pendiente tampoco debe caer primero en la portada pública al volver. */
function RootEntry() {
  const { state } = useAuth();
  const home = isStandalone() ? <Navigate to="/inicio" replace /> : <HomePage />;
  if (state.kind === "unknown" && getAccessToken()) return <EsperaDePantalla />;
  if (state.kind === "authenticated" && tieneRespuestaPendiente(state.profile.email)) {
    return <ProtectedRoute requireLeague={false}>{home}</ProtectedRoute>;
  }
  return home;
}


export function App() {
  // Boundary a nivel de ruta: un error de render de cualquier página no tira
  // toda la app (pantalla en blanco), y al navegar a otra pantalla el error se
  // olvida.
  //
  // Va por `resetKey` y NO por `key`. Con `key={pathname}` React desmontaba y
  // volvía a montar TODO el árbol en cada navegación: en el flujo jugador eso
  // significaba que cambiar de pestaña tiraba abajo el PlayerShell —que es una
  // ruta padre y debería quedarse quieto— y lo volvía a construir, repidiendo
  // /me/contexts y /me/player-profile y repintando el fondo y la barra, además
  // de lo que pide la pestaña nueva. El reseteo del error es el mismo; lo que
  // se va es el desmontaje.
  const location = useLocation();
  // La PWA instalada arranca como la app Android: directo al login (o al panel
  // si ya hay sesión). La landing es para el que llega a forjaligas.com a
  // conocer FORJA; quien la instaló ya la conoce, y el start_url "/" le metía
  // la vitrina entera antes de poder entrar. /inicio resuelve las dos cosas
  // (ProtectedRoute manda al login y el login vuelve ahí).
  const raiz = <RootEntry />;

  // Los links que ABREN la app (forjaligas.com/unirse/…). iOS le entrega la
  // URL al contenedor y, si nadie la escucha, la app arranca en su pantalla de
  // siempre y el link se pierde en silencio. Ver src/lib/linksDeLaApp.ts.
  const navigate = useNavigate();
  useEffect(() => {
    engancharLinksDeLaApp((ruta) => navigate(ruta));
  }, [navigate]);

  return (
    <ErrorBoundary resetKey={location.pathname}>
      <PwaUpdateNotice />
      {/* Sesión VER COMO del superusuario: si esta pestaña la tiene, el banner
          ámbar queda fijo arriba en TODAS las pantallas. */}
      <VerComoBanner />
      <BiometricLockGate>
      <Suspense fallback={<EsperaDePantalla />}>
      <Routes>
      {/* Entrada del VER COMO (el panel admin la abre con el token en el hash). */}
      <Route path="/ver-como" element={<VerComoEntrada />} />
      {/* Pase de la app: abre la consola ya logueado (el pase va en el hash). */}
      <Route path="/entrar-app" element={<EntrarAppEntrada />} />
      {/* Ruta publica del torneo (sin Layout/Auth) */}
      {/* Las DOS formas del enlace público. El de GUID queda para siempre:
          cualquiera pudo compartirlo por WhatsApp, y un enlace de torneo que
          deja de andar es de los errores que no se perdonan. */}
      <Route path="/p/:tournamentId" element={<PublicTournamentPage />} />
      <Route path="/p/:tournamentId/:tournamentSlug" element={<PublicTournamentPage />} />
      {/* Link de pago que FORJA le manda a la liga (PayPal: tarjeta o cuenta).
          Público: lo abre el tesorero, sin cuenta. El token es la llave. */}
      <Route path="/pagar/:token" element={<PagarPage />} />

      {/* FLUJO JUGADOR (PWA, look FORJA propio — sin el Layout del organizador).
          Área de Claude: ver AGENTS.md antes de tocar. */}
      {/* Canje de invitación: el preview es público, el login se pide adentro. */}
      {/* Autodiagnóstico del aparato: PÚBLICO a propósito — lo abre quien
          preste un iPhone dos minutos, sin cuenta ni explicaciones. */}
      <Route path="/diagnostico" element={<PlayerDiagnosticoPage />} />
      {/* Landing del mail de designación de árbitro: pública y sin Layout —
          con la app instalada Android la intercepta como App Link y ni se ve. */}
      <Route path="/arbitro/partido/:matchId" element={<RefereeMatchLinkPage />} />
      {/* FLUJO ÁRBITRO (PWA) — la puerta iPhone del rol: agenda cross-liga y
          carga MANUAL del acta contra /api/v1/referee/* (autoriza la
          DESIGNACIÓN, no un rol de liga). El modo vivo con voz sigue siendo de
          la app Android. Rutas sueltas a propósito: anidarlas bajo /arbitro
          metería la landing pública de arriba dentro del ProtectedRoute.
          Área de Claude: ver AGENTS.md antes de tocar. */}
      <Route
        path="/arbitro"
        element={
          <ProtectedRoute requireLeague={false}>
            <RefereeAgendaPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/arbitro/perfil"
        element={
          <ProtectedRoute requireLeague={false}>
            <RefereePerfilPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/arbitro/partido/:matchId/cargar"
        element={
          <ProtectedRoute requireLeague={false}>
            <RefereeMatchPage />
          </ProtectedRoute>
        }
      />
      <Route path="/unirse" element={<PlayerJoinPage />} />
      {/* La puerta del que NO juega: amigos y familia. Separada de /unirse
          porque lo que hace es distinto —seguir, no ficharse— y porque los dos
          links circulan por el mismo grupo de WhatsApp (ver 088). */}
      <Route path="/hincha" element={<FanJoinPage />} />
      {/* MI EQUIPO SIN CUENTA. La puerta de antes del registro: buscás por el
          nombre del equipo y mirás fixture, resultados y puesto. La cuenta se
          pide recién para SEGUIRLO. Pública a propósito y sin App Link: es
          justo la pantalla del que todavía no tiene la app. */}
      <Route path="/buscar" element={<PublicTeamPage />} />
      <Route path="/equipo/:teamId" element={<PublicTeamPage />} />
      {/* Bajarse la app: a donde apunta el QR que se reparte a los equipos.
          Android → grupo de prueba + opt-in + Play; iPhone → la PWA. Pública
          y SIN App Link a propósito: el que ya tiene la app también tiene que
          poder ver los pasos para pasárselos al compañero. */}
      <Route path="/descargar" element={<DownloadAppPage />} />
      {/* Vista aislada de la pestaña Voz del panel (sin backend ni sesión), para
          poder revisarla en el navegador. Solo en desarrollo: en producción la
          rama se elimina al compilar. */}
      {import.meta.env.DEV && <Route path="/vista-voz" element={<VistaVozPreview />} />}
      {import.meta.env.DEV && <Route path="/vista-fecha-oculta" element={<VistaFechaOcultaPreview />} />}
      {import.meta.env.DEV && <Route path="/vista-figura" element={<VistaFiguraPreview />} />}
      {import.meta.env.DEV && <Route path="/vista-asistencia" element={<VistaAsistenciaPreview />} />}
      {import.meta.env.DEV && <Route path="/vista-equipo-ideal" element={<VistaEquipoIdealPreview />} />}
      {import.meta.env.DEV && <Route path="/vista-card" element={<VistaCardPreview />} />}
      {import.meta.env.DEV && <Route path="/vista-card-jugador" element={<VistaCardJugadorPreview />} />}
      {import.meta.env.DEV && <Route path="/vista-reverso" element={<VistaReversoPreview />} />}
      {import.meta.env.DEV && <Route path="/vista-organizador" element={<VistaOrganizadorPreview />} />}
      {import.meta.env.DEV && <Route path="/vista-cotizador" element={<VistaCotizadorPreview />} />}
      {import.meta.env.DEV && <Route path="/vista-cobros" element={<VistaCobrosPreview />} />}
      {import.meta.env.DEV && <Route path="/vista-pagar" element={<VistaPagarPreview />} />}
      {import.meta.env.DEV && <Route path="/vista-incidencias/:matchId" element={<VistaIncidenciasPreview />} />}
      {import.meta.env.DEV && <Route path="/vista-comunidad" element={<VistaComunidadPreview />} />}
      {import.meta.env.DEV && <Route path="/vista-comunidad/chat/:conversationId" element={<VistaComunidadPreview />} />}
      {import.meta.env.DEV && <Route path="/vista-perfil-comunidad" element={<VistaPerfilComunidadPreview />} />}
      {import.meta.env.DEV && <Route path="/vista-carga-partido" element={<VistaCargaPartidoPreview />} />}
      {import.meta.env.DEV && <Route path="/vista-resultado-web" element={<VistaResultadoWebPreview />} />}
      {/* El :token va en la RUTA porque PlayerJoinPage lo lee con useParams:
          la vista tiene que entrar por el mismo camino que /unirse/CODIGO. */}
      {import.meta.env.DEV && <Route path="/vista-unirse/:token" element={<VistaUnirsePreview />} />}
      {/* SIN token: la otra puerta, pedir entrar por equipo
          (/vista-unirse?equipo=lo-que-sea), que es como entra /unirse?equipo=. */}
      {import.meta.env.DEV && <Route path="/vista-unirse" element={<VistaUnirsePreview />} />}
      {/* Ruta PADRE con la pantalla de hija: el Outlet de PlayerShell resuelve
          contra el árbol de RUTAS, así que el cabezal recibe su context igual
          que en /jugador — que es justo lo que hay que poder mirar acá. */}
      {import.meta.env.DEV && (
        <Route path="/vista-foto-perfil" element={<VistaFotoPerfilPreview />}>
          <Route index element={<PlayerProfilePage />} />
        </Route>
      )}
      {import.meta.env.DEV && <Route path="/vista-disciplina" element={<VistaDisciplinaPreview />} />}
      {import.meta.env.DEV && <Route path="/vista-cuerpo-tecnico" element={<VistaCuerpoTecnicoPreview />} />}
      {import.meta.env.DEV && <Route path="/vista-plantel-panel" element={<VistaPlantelPanelPreview />} />}
      {import.meta.env.DEV && <Route path="/vista-arbitros" element={<VistaArbitrosPreview />} />}
      {import.meta.env.DEV && <Route path="/vista-agenda-arbitro" element={<VistaAgendaArbitroPreview />} />}
      {import.meta.env.DEV && <Route path="/vista-fotos-archivo" element={<VistaFotosArchivoPreview />} />}
      {import.meta.env.DEV && <Route path="/vista-torneos" element={<VistaTorneosPreview />} />}
      {import.meta.env.DEV && <Route path="/vista-torneo-nuevo" element={<VistaTorneoNuevoPreview />} />}
      {import.meta.env.DEV && <Route path="/vista-escudo-equipo" element={<VistaEscudoEquipoPreview />} />}
      {import.meta.env.DEV && <Route path="/vista-editar-torneo" element={<VistaEditarTorneoPreview />} />}
      {import.meta.env.DEV && <Route path="/vista-fase-final" element={<VistaFaseFinalPreview />} />}
      {import.meta.env.DEV && <Route path="/vista-lo-que-sigue" element={<VistaLoQueSiguePreview />} />}
      {import.meta.env.DEV && <Route path="/vista-panel-torneo/:tournamentId" element={<VistaPanelTorneoPreview />} />}
      {import.meta.env.DEV && <Route path="/vista-siguiente-torneo" element={<VistaSiguienteTorneoPreview />} />}
      {import.meta.env.DEV && <Route path="/vista-editar-cruce" element={<VistaEditarCrucePreview />} />}
      {import.meta.env.DEV && <Route path="/vista-planilla" element={<VistaPlanillaPreview />} />}
      {import.meta.env.DEV && <Route path="/vista-planillas-export" element={<VistaPlanillasExportPreview />} />}
      {import.meta.env.DEV && <Route path="/vista-importar-fecha" element={<VistaImportarFechaPreview />} />}
      {import.meta.env.DEV && <Route path="/vista-desprogramar" element={<VistaDesprogramarPreview />} />}
      {import.meta.env.DEV && <Route path="/vista-programar-fecha" element={<VistaProgramarFechaPreview />} />}
      {import.meta.env.DEV && <Route path="/vista-jornada-panel" element={<VistaJornadaPanelPreview />} />}
      {import.meta.env.DEV && <Route path="/vista-pwa-programacion" element={<VistaPwaProgramacionPreview />} />}
      {import.meta.env.DEV && <Route path="/vista-auto-programar" element={<VistaAutoProgramarPreview />} />}

      {import.meta.env.DEV && <Route path="/vista-grupos-fecha" element={<VistaGruposFechaPreview />} />}
      {import.meta.env.DEV && <Route path="/vista-fixture-vacio" element={<VistaFixtureVacioPreview />} />}
      {import.meta.env.DEV && <Route path="/vista-avisos" element={<VistaAvisosPreview />} />}
      {import.meta.env.DEV && <Route path="/vista-selector-torneo" element={<VistaSelectorTorneoPreview />} />}
      {/* Cómo se ve un partido que nunca se jugó pero cuenta (W.O. por retiro). */}
      {import.meta.env.DEV && <Route path="/vista-retiro" element={<VistaRetiroPreview />} />}
      {/* Home del jugador contra la captura de Android: se audita el TAMAÑO. */}
      {import.meta.env.DEV && <Route path="/vista-home-jugador" element={<VistaHomeJugadorPreview />} />}
      {/* La bienvenida del que no está en ninguna liga (el hincha sin código). */}
      {import.meta.env.DEV && <Route path="/vista-bienvenida" element={<VistaBienvenidaPreview />} />}
      {import.meta.env.DEV && <Route path="/vista-liga-jugador" element={<VistaLigaJugadorPreview />} />}
      {import.meta.env.DEV && <Route path="/vista-partido-jugador/:matchId" element={<VistaPartidoJugadorPreview />} />}
      {import.meta.env.DEV && <Route path="/vista-tarjeta-jugador" element={<VistaTarjetaJugadorPreview />} />}
      {import.meta.env.DEV && <Route path="/vista-tarjetas-liga" element={<VistaTarjetasLigaPreview />} />}
      {import.meta.env.DEV && <Route path="/vista-encuadre-tope" element={<VistaEncuadreTopePreview />} />}
      {import.meta.env.DEV && <Route path="/vista-equipo-jugador" element={<VistaEquipoJugadorPreview />} />}
      {import.meta.env.DEV && <Route path="/vista-foto-equipo" element={<VistaFotoEquipoPreview />} />}
      {import.meta.env.DEV && <Route path="/vista-banco-fotos/:teamId" element={<VistaBancoFotosPreview />} />}
      {/* Flujo árbitro sin backend ni designación real: agenda y partido. */}
      {import.meta.env.DEV && <Route path="/vista-arbitro-flujo" element={<VistaArbitroPreview />} />}
      {import.meta.env.DEV && <Route path="/vista-arbitro-flujo/:matchId" element={<VistaArbitroPreview />} />}
      {import.meta.env.DEV && <Route path="/vista-admin-ligas" element={<VistaAdminLigasPreview />} />}
      {import.meta.env.DEV && <Route path="/vista-borrar-liga" element={<VistaBorrarLigaPreview />} />}
      {/* Panel de plataforma (todas las ligas). El permiso lo valida el
          backend: users.is_platform_admin, migración 065. */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute requireLeague={false}>
            <AdminPage />
          </ProtectedRoute>
        }
      />
      {/* Alta con email: la puerta del que no usa Google. */}
      <Route path="/registro" element={<PlayerRegisterPage />} />
      <Route path="/unirse/:token" element={<PlayerJoinPage />} />
      <Route path="/hincha/:token" element={<FanJoinPage />} />
      {/* Invitación NOMINADA de organizador (llega por email): preview público,
          aceptar exige sesión con el email invitado. Path propio a propósito:
          /unirse, /hincha e /i los intercepta la app Android como App Link, y
          esta puerta es de la web. Área de Claude: ver AGENTS.md. */}
      <Route path="/invitacion/:token" element={<OrganizerInviteJoinPage />} />
      {/* requireLeague={false}: el jugador recién registrado NO tiene contexto
          todavía y el shell ya maneja ese caso (PlayerWelcomePage). Con el
          default caía en /onboarding, la pantalla "Creá tu liga" del
          organizador — visto en prod, ago 2026. */}
      <Route
        path="/jugador"
        element={
          <ProtectedRoute requireLeague={false}>
            <PlayerShell />
          </ProtectedRoute>
        }
      >
        <Route index element={<PlayerHomePage />} />
        <Route path="liga" element={<PlayerLeaguePage />} />
        <Route path="tarjeta" element={<PlayerCardPage />} />
        <Route path="instalar" element={<PlayerInstallPage />} />
        <Route path="partido/:matchId" element={<PlayerMatchPage />} />
        <Route path="equipo" element={<PlayerTeamPage />} />
        <Route path="equipo/:teamId" element={<PlayerTeamPage />} />
        {/* Banco de fotos: "Encontrá tu foto" (onboarding, banner del home y
            el push "Ya están las fotos de tu equipo" llegan acá). */}
        <Route path="fotos-equipo/:teamId" element={<PlayerPhotoPickPage />} />
        {/* Ficha pública de un jugador (se llega desde la ficha del plantel
            o de un ranking), igual que en la app. */}
        <Route path="ficha/:playerId" element={<PublicPlayerProfilePage />} />
        {/* Comunidad: el tablón + las conversaciones que salen de él. */}
        <Route path="perfil" element={<PlayerProfilePage />} />
        <Route path="comunidad" element={<CommunityPage />} />
        <Route path="comunidad/perfil" element={<CommunityProfilePage />} />
        <Route path="comunidad/mensajes" element={<CommunityMessagesPage />} />
        <Route path="comunidad/mensajes/:conversationId" element={<CommunityChatPage />} />
      </Route>

      {/* FLUJO ORGANIZADOR EN FORMA DE APP (PWA — sin el Layout de escritorio).
          Espejo del cockpit de Android: Inicio / Fecha / Liga / Actividad /
          Admin. Es un subconjunto de cancha; el panel completo sigue en
          /tournaments y se llega desde el cabezal.
          Área de Claude: ver AGENTS.md antes de tocar. */}
      {/* El editor del equipo ideal SOLO, sin el shell: es lo que la app
          Android muestra adentro suyo (ver OrganizerEquipoIdealStandalone). */}
      <Route
        path="/organizador/equipo-ideal/:tournamentId/:matchdayId"
        element={
          <ProtectedRoute soloOrganizador>
            <OrganizerEquipoIdealStandalone />
          </ProtectedRoute>
        }
      />
      {/* Vista aislada del escudo solo (SOLO desarrollo): la pantalla real
          vive detrás de sesión de organizador, y mirar el encuadre y los
          textos no necesita backend. Guardar sí falla: eso se prueba con
          sesión. */}
      {import.meta.env.DEV && (
        <Route path="/vista-escudo/:id" element={<OrganizerEscudoStandalone />} />
      )}
      {/* El escudo (liga o equipo) SOLO, sin el shell: el recortador V2 vive
          en el navegador, así que la app Android muestra esta pantalla adentro
          suyo en vez de tener un segundo pipeline en Kotlin. */}
      <Route
        path="/organizador/escudo/liga/:id"
        element={
          <ProtectedRoute soloOrganizador>
            <OrganizerEscudoStandalone />
          </ProtectedRoute>
        }
      />
      <Route
        path="/organizador/escudo/equipo/:tournamentId/:id"
        element={
          <ProtectedRoute soloOrganizador>
            <OrganizerEscudoStandalone />
          </ProtectedRoute>
        }
      />
      <Route
        path="/organizador"
        element={
          <ProtectedRoute soloOrganizador>
            <OrganizerAppShell />
          </ProtectedRoute>
        }
      >
        <Route index element={<OrganizerInicioPage />} />
        {/* El push "Fotos para revisar": Inicio con la capa Fotos y tarjetas abierta. */}
        <Route path="fotos" element={<OrganizerInicioPage />} />
        <Route path="fecha" element={<OrganizerFechaPage />} />
        <Route path="liga" element={<OrganizerLigaPage />} />
        <Route path="actividad" element={<OrganizerActividadPage />} />
        <Route path="admin" element={<Navigate to="/organizador/liga" replace />} />
      </Route>

      <Route element={<Layout />}>
        {import.meta.env.DEV && <Route path="/vista-mensaje-obligatorio" element={<VistaMensajeObligatorioPreview />} />}
        {import.meta.env.DEV && <Route path="/vista-gate-obligatorio" element={<VistaGateObligatorioPreview />} />}
        {import.meta.env.DEV && <Route path="/tournaments/vista-tema" element={<div />} />}
        {import.meta.env.DEV && (
          <Route
            path="/tournaments/vista-editar-torneo"
            element={(
              <EditTournamentModal
                tournamentId="preview"
                initialName="Liga Empresarial"
                initialSeason="2026"
                initialStatusCode="active"
                initialFormatCode="round_robin"
                initialYellowCards={5}
                initialRedMatches={2}
                initialRosterMax={18}
                initialMatchFormat="F5"
                initialScheduleConfig={{ weekdays: [0], startTimes: ["19:00", "20:00", "21:00"], venueIds: [] }}
                pendingMatches={3}
                onClose={() => undefined}
                onSaved={() => undefined}
              />
            )}
          />
        )}
        <Route path="/" element={raiz} />
        <Route path="/login" element={<LoginPage />} />
        {/* Destino post-login: decide por los roles de la liga activa. */}
        <Route
          path="/inicio"
          element={
            <ProtectedRoute requireLeague={false}>
              <InicioRedirect />
            </ProtectedRoute>
          }
        />
        <Route path="/terminos" element={<TermsPage />} />
        <Route path="/privacidad" element={<PrivacyPage />} />
        {/* La exige Google Play (Data Safety): URL pública con los pasos para
            pedir la eliminación de la cuenta. */}
        <Route path="/eliminar-cuenta" element={<AccountDeletionPage />} />
        {/* Instalar la web como app en el celular del que carga los resultados.
            PÚBLICA a propósito: se comparte por WhatsApp al que va a la cancha,
            y tiene que poder abrirla antes de loguearse. */}
        <Route path="/instalar" element={<InstallPage />} />
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute requireLeague={false}>
              <OnboardingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tournaments"
          element={
            <ProtectedRoute soloOrganizador>
              <TournamentsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tournaments/new"
          element={
            <ProtectedRoute soloOrganizador>
              <NewTournamentPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tournaments/:tournamentId"
          element={
            <ProtectedRoute soloOrganizador>
              <TournamentWorkspacePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/fotos"
          element={
            <ProtectedRoute soloOrganizador>
              <FotosPanelPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/jornada"
          element={
            <ProtectedRoute soloOrganizador>
              <JornadaPanelPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tournaments/:tournamentId/jornada"
          element={
            <ProtectedRoute soloOrganizador>
              <JornadaPanelPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute requireLeague={false}>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/mensajes"
          element={
            <ProtectedRoute requireLeague={false}>
              <InternalMessagesPage />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={raiz} />
      </Route>
      </Routes>
      </Suspense>
      </BiometricLockGate>
    </ErrorBoundary>
  );
}
